import { useState } from 'react';
import type { AlertRow, TelemetryPoint } from '@glyph/protocol-ts';
import { fmtAgo, fmtDur, fmtMs, useAlerts, useCan, useCommand, useDevice, useEvents, useLive, useMe, useStatus, useTelemetryHistory } from '@glyph/core';
import { Button, Card, CardHeader, Checkbox, CloudIcon, DeepgramMark, Dialog, EmptyState, FactRow, IconButton, PageHeader, PencilIcon, Section, SegmentedControl, Select, Tag, TimeSeriesChart, severityTone, stateTone, useToast } from '@glyph/ui';
import { UPLINK_LABEL, fmtTs } from './shared';
import { RenameDeviceDialog } from './RenameDeviceDialog';

export interface OverviewScreenProps {
  deviceId?: string;
}

/** Validated chart hues (dataviz palette check, dark + light surfaces): download orange, upload blue. */
const DOWN = '#D9601A';
const UP = '#2B86D9';
/** Same validation, second chart: cloud round trip green, Deepgram round trip violet. */
const CLOUD = '#2E9E5B';
const DEEPGRAM = '#9A62D9';
const RANGES = [
  { value: '5', label: '5 min' },
  { value: '30', label: '30 min' },
  { value: '60', label: '1 h' },
  { value: '360', label: '6 h' },
  { value: '1440', label: '24 h' },
] as const;
/** What the unit is doing about updates, from its last update status: shown next to the state everywhere on this page. */
export function updateNote(d: { update_state: string | null; update_target: string | null; update_pct: number | null; update_error: string | null; update_finished_at: number | null; agent_version: string }, now = Date.now() / 1000): string {
  const s = d.update_state;
  const v = d.update_target ?? '';
  if (!s) return '';
  if (['checking', 'downloading', 'verifying', 'extracting'].includes(s)) return `DOWNLOADING ${v}${d.update_pct != null && s === 'downloading' ? ` · ${Math.round(d.update_pct)} %` : ''}`.trim();
  if (s === 'staged') return `${v} STAGED · NOT INSTALLED YET`;
  if (['installing', 'switching', 'restarting'].includes(s)) return `INSTALLING ${v}`;
  if (s === 'healthchecking') return `HEALTH CHECK ON ${d.agent_version || v} · ROLLS BACK IF IT FAILS`;
  if (s === 'success' && d.update_finished_at && now - d.update_finished_at < 30 * 60) return `UPDATED TO ${d.agent_version || v}`;
  if (s === 'rollback') return `ROLLED BACK${d.update_error ? ` · ${d.update_error}` : ''}`;
  if (s === 'failed') return `UPDATE FAILED${d.update_error ? ` · ${d.update_error}` : ''}`;
  return '';
}

/**
 * Cloud-only Overview of one unit: latency to the cloud and to Deepgram, network traffic over the last hour,
 * system health, open alerts for this unit, and the power/maintenance actions (same as the unit's System page).
 */
export function OverviewScreen({ deviceId }: OverviewScreenProps) {
  const dev = useDevice(deviceId);
  const status = useStatus(deviceId);
  const live = useLive(deviceId);
  const [range, setRange] = useState<string>('60');
  const [clip, setClip] = useState(true);
  const hist = useTelemetryHistory(deviceId, Number(range));
  const rangeLabel = RANGES.find((r) => r.value === range)?.label ?? '1 h';
  const shows = useEvents();
  const me = useMe();
  const canAssign = me.data?.role === 'platform';
  const canRename = useCan('device.rename');
  const [renaming, setRenaming] = useState(false);
  const alerts = useAlerts(true);
  const cmd = useCommand(deviceId);
  const toast = useToast();
  const canPower = useCan('system.reboot');
  const [power, setPower] = useState<null | 'reboot' | 'shutdown'>(null);
  const d = dev.data;
  const st = status.data;
  const t = live.telemetry;
  const online = !!d?.online;
  const cloudMs = d?.cloud_rtt_ms ?? null;
  const dgMs = st?.engine?.rtt_ms ?? null;
  const points = (hist.data ?? []) as TelemetryPoint[];
  const ts = (p: TelemetryPoint) => Math.floor(Date.parse(p.ts) / 1000);
  const last = points[points.length - 1];
  const mine = (alerts.data ?? []).filter((a: AlertRow) => a.device_id === d?.id);
  const captions = !online ? 'Offline' : (st?.engine?.running ?? t?.engine_running) ? 'Running' : 'Idle';
  const note = d ? updateNote(d) : '';
  const assign = async (v: string) => {
    if (!d) return;
    try {
      await shows.assign({ deviceId: d.id, eventId: v || null });
      toast.push(v ? `${d.name} → ${shows.data?.find((e) => e.id === v)?.name ?? 'group'}` : `${d.name} removed from its group`, 'ok');
      void dev.refetch();
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const groupOptions = [{ value: '', label: 'Unassigned' }, ...(shows.data ?? []).map((e) => ({ value: e.id, label: e.name }))];
  const nameValue = (
    <span className="group inline-flex items-center gap-1.5">
      {d?.name ?? '—'}
      {canRename && (
        <IconButton framed={false} label={`Rename ${d?.name ?? 'unit'}`} onClick={() => setRenaming(true)} className="opacity-70 transition-opacity hover:opacity-100 focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100">
          <PencilIcon />
        </IconButton>
      )}
    </span>
  );
  const run = async (action: 'system.identify' | 'system.selftest' | 'engine.restart') => {
    try {
      const r = await cmd.run(action, {});
      if (action === 'system.selftest' && r) toast.push((r as { passed?: boolean }).passed ? 'Self-test passed' : 'Self-test failed', (r as { passed?: boolean }).passed ? 'ok' : 'bad');
      else toast.push(`${action.split('.')[1]} sent`, 'ok');
    } catch (e) {
      toast.push((e as Error).message, 'bad');
    }
  };
  const latency = (
    <Card>
      <CardHeader title="Latency" meta={online ? 'ROUND TRIPS' : 'OFFLINE'} />
      <div className="grid grid-cols-2 divide-x divide-line border-t border-line">
        <div className="flex flex-col gap-1 px-4 py-3">
          <span className="micro flex items-center gap-1.5 text-fg4">
            <CloudIcon /> Cloud
          </span>
          <span className="font-mono text-2xl tabular">{online ? fmtMs(cloudMs) : '—'}</span>
          <span className="font-mono text-xs text-fg4">cloud ↔ unit</span>
        </div>
        <div className="flex flex-col gap-1 px-4 py-3">
          <span className="micro flex items-center gap-1.5 text-fg4">
            <DeepgramMark /> Deepgram
          </span>
          <span className="font-mono text-2xl tabular">{online ? fmtMs(dgMs) : '—'}</span>
          <span className="font-mono text-xs text-fg4">audio sent → words back</span>
        </div>
      </div>
    </Card>
  );
  const facts = (
    <Card>
      <CardHeader title="Status" meta={d ? (online ? 'ONLINE' : d.last_seen ? `SEEN ${fmtAgo(d.last_seen).toUpperCase()}` : 'NEVER SEEN') : ''} metaTone={online ? 'ok' : 'warn'} />
      <FactRow label="Name" value={nameValue} />
      <FactRow label="Captions" value={captions} valueTone={captions === 'Running' ? 'ok' : undefined} />
      <FactRow label="Group" value={canAssign && d ? <Select size="sm" value={d.assignment?.event_id ?? ''} options={groupOptions} onChange={(v) => void assign(v)} aria-label="Group" disabled={shows.busy} /> : (d?.assignment?.event_name ?? 'Unassigned')} />
      {note && <FactRow label="Update" value={note} meta valueTone={d?.update_state === 'failed' || d?.update_state === 'rollback' ? 'warn' : 'accent'} />}
      <FactRow label="Uplink" value={online ? `${UPLINK_LABEL[d?.uplink ?? 'none']}${d?.address ? ` · ${d.address}` : ''}` : '—'} />
      <FactRow label="Hotspot" value={d?.hotspot_ssid ?? '—'} />
      <FactRow label="Firmware" value={`${d?.agent_version || '—'}${st?.version?.slot ? ` · slot ${st.version.slot}` : ''}`} />
      <FactRow label="Uptime" value={online && d ? fmtDur(d.uptime_s) : '—'} />
    </Card>
  );
  const net = (
    <Card>
      <CardHeader title="Network" meta={`LAST ${rangeLabel.toUpperCase()} · KBPS`} />
      <div className="px-4 pb-3 pt-2">
        <TimeSeriesChart
          series={[
            { key: 'down', label: 'Download', color: DOWN, points: points.map((p) => ({ t: ts(p), v: p.net_rx_kbps })) },
            { key: 'up', label: 'Upload', color: UP, points: points.map((p) => ({ t: ts(p), v: p.net_tx_kbps })) },
          ]}
          unit=" kbps"
          clipSpikes={clip}
          empty={hist.isLoading ? 'Loading…' : `No telemetry in the last ${rangeLabel}`}
        />
      </div>
    </Card>
  );
  const latencyChart = (
    <Card>
      <CardHeader title="Round trips" meta={`LAST ${rangeLabel.toUpperCase()} · MS`} />
      <div className="px-4 pb-3 pt-2">
        <TimeSeriesChart
          series={[
            { key: 'cloud', label: 'Cloud', color: CLOUD, points: points.map((p) => ({ t: ts(p), v: p.cloud_rtt_ms })) },
            { key: 'dg', label: 'Deepgram', color: DEEPGRAM, points: points.map((p) => ({ t: ts(p), v: p.dg_latency_ms })) },
          ]}
          unit=" ms"
          clipSpikes={clip}
          empty={hist.isLoading ? 'Loading…' : `No telemetry in the last ${rangeLabel}`}
        />
      </div>
    </Card>
  );
  const sys = (
    <Card>
      <CardHeader title="System" meta={last ? `AT ${fmtTs(ts(last))}` : ''} />
      <FactRow label="CPU" value={t?.cpu_pct != null ? `${Math.round(t.cpu_pct)} %` : last?.cpu_pct != null ? `${Math.round(last.cpu_pct)} %` : '—'} />
      <FactRow label="Temperature" value={t?.temp_c != null ? `${Math.round(t.temp_c)} °C` : last?.temp_c != null ? `${Math.round(last.temp_c)} °C` : '—'} />
      <FactRow label="Memory" value={t?.mem_pct != null ? `${Math.round(t.mem_pct)} % used` : last?.mem_pct != null ? `${Math.round(last.mem_pct)} % used` : '—'} />
      <FactRow label="Disk free" value={t?.disk_free_gb != null ? `${t.disk_free_gb.toFixed(1)} GB` : last?.disk_free_mb != null ? `${(last.disk_free_mb / 1024).toFixed(1)} GB` : '—'} />
      <FactRow label="Power" value={t?.under_voltage ? 'UNDER-VOLTAGE' : t?.throttled ? 'THROTTLED' : online ? 'OK' : '—'} meta valueTone={t?.under_voltage || t?.throttled ? 'warn' : online ? 'ok' : undefined} />
    </Card>
  );
  const alertsCard = (
    <Card>
      <CardHeader title="Open alerts" meta={`${mine.length} OPEN`} metaTone={mine.length ? 'warn' : 'ok'} />
      {mine.length === 0 && <EmptyState label="Nothing open for this unit" />}
      {mine.slice(0, 6).map((a) => (
        <div key={a.id} className="flex items-center gap-3 border-t border-line px-4 py-2.5">
          <span className="font-mono text-sm text-fg4 tabular">{fmtTs(a.ts)}</span>
          <Tag tone={severityTone(a.severity)}>{a.severity}</Tag>
          <span className="min-w-0 flex-1 truncate text-base text-fg">{a.detail}</span>
          <Button size="xs" mono onClick={() => void alerts.ack(a)} aria-label="Close alert">
            CLOSE
          </Button>
        </div>
      ))}
    </Card>
  );
  const actions = (
    <Card>
      <CardHeader title="Actions" meta={online ? '' : 'UNIT OFFLINE'} />
      <div className="flex flex-wrap gap-2 px-4 py-3.5">
        <Button size="responsive" disabled={!online} onClick={() => void run('system.identify')}>
          Identify
        </Button>
        <Button size="responsive" disabled={!online} onClick={() => void run('system.selftest')}>
          Self-test
        </Button>
        <Button size="responsive" disabled={!online} onClick={() => void run('engine.restart')}>
          Restart captions
        </Button>
        {canPower && (
          <>
            <Button size="responsive" disabled={!online} onClick={() => setPower('reboot')}>
              Reboot
            </Button>
            <Button size="responsive" variant="destructive-outline" disabled={!online} onClick={() => setPower('shutdown')}>
              Shut down
            </Button>
          </>
        )}
      </div>
    </Card>
  );
  return (
    <Section>
      <PageHeader
        desktopOnly
        title={nameValue}
        badge={
          <Tag tone={stateTone(d?.state ?? 'offline')} large>
            {d?.state ?? 'offline'}
          </Tag>
        }
        meta={`${d?.assignment?.event_name ?? 'Unassigned'} · fw ${d?.agent_version || '—'} · ${online ? `up ${fmtDur(d?.uptime_s ?? 0)}` : d?.last_seen ? `seen ${fmtAgo(d.last_seen)}` : 'never seen'}${note ? ` · ${note}` : ''}`}
      />
      <RenameDeviceDialog open={renaming} onClose={() => setRenaming(false)} deviceId={d?.id} currentName={d?.name ?? ''} serial={d?.serial} currentSsid={d?.hotspot_ssid} />
      {/* left: what the unit is · centre: the charts · right: how it is reached and what is wrong */}
      {/* Phone: one column in reading order (latency, charts, actions, then status, system, alerts). Desktop: the
          three stacks (`contents` lets the cards order themselves in the single column below lg). */}
      <div className="grid items-start gap-4 md:gap-5 lg:grid-cols-[minmax(260px,1fr)_minmax(0,2fr)_minmax(260px,1fr)]">
        <div className="contents lg:flex lg:flex-col lg:gap-5">
          <div className="order-5 min-w-0 lg:order-none">{facts}</div>
          <div className="order-6 min-w-0 lg:order-none">{sys}</div>
          <div className="order-4 min-w-0 lg:order-none">{actions}</div>
        </div>
        <div className="contents lg:flex lg:flex-col lg:gap-5">
          <div className="order-2 flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 lg:order-none">
            <SegmentedControl fullWidth value={range} onChange={setRange} options={RANGES} />
            <label className="inline-flex flex-none cursor-pointer items-center gap-2 self-end sm:self-auto">
              <Checkbox checked={clip} onChange={setClip} label="Clip spikes" />
              <span className="text-md text-fg2">Clip spikes</span>
            </label>
          </div>
          <div className="order-2 min-w-0 lg:order-none">{net}</div>
          <div className="order-3 min-w-0 lg:order-none">{latencyChart}</div>
        </div>
        <div className="contents lg:flex lg:flex-col lg:gap-5">
          <div className="order-1 min-w-0 lg:order-none">{latency}</div>
          <div className="order-7 min-w-0 lg:order-none">{alertsCard}</div>
        </div>
      </div>
      <Dialog
        open={!!power}
        onClose={() => setPower(null)}
        title={power === 'reboot' ? `Reboot ${d?.name ?? 'this unit'}?` : `Shut down ${d?.name ?? 'this unit'}?`}
        meta={power === 'reboot' ? 'CAPTIONS DROP FOR ~30 S · HDMI RE-SYNCS' : 'OLED TURNS OFF · SOMEONE MUST PULL POWER TO RESTART'}
        confirm={{
          label: power === 'reboot' ? 'Reboot' : 'Shut down',
          destructive: true,
          onClick: async () => {
            try {
              await cmd.run(power === 'reboot' ? 'system.reboot' : 'system.shutdown', power === 'reboot' ? { confirm: true } : {});
            } catch (e) {
              toast.push((e as Error).message, 'bad');
            }
            setPower(null);
          },
        }}
      >
        {power === 'reboot' ? 'The renderer boots straight to the boot colour and the session resumes.' : 'A clean shutdown closes the recording session and unmounts the data volume.'}
      </Dialog>
    </Section>
  );
}
